import "./styles.css";
import { FC, useState } from "react";
import { useFormik } from "formik";
import { Box, Grid } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import useMutation from "../../../../../shared/components/hooks/useMutation";
import useQuery from "../../../../../shared/components/hooks/useQuery";
import InputTextfield from "../../../../../shared/components/textField";
import { FormContainer } from "../../../../../shared/components/formContainer";
import { ViewContainer } from "../../../../../shared/components/viewContainer";
import SingleEntityContainer from "../../../../../shared/components/singleEntityContainer";
import { TitleBarProps } from "../../../../../shared/components/titleBar";
import { getTextFields } from "../variables/textFields";
import { customizeValidationSchema } from "../variables/customSchema";
import ToggleSwitch from "../../../../../shared/components/switch";
import { Qualification } from "../../../../../shared/interfaces/qualification";
import { ZSSelectBox } from "../../../../../shared/components/selectBox";
import { QualificationType } from "../../../../../shared/enum";
import { EditButton } from "../../../../../shared/components/editButton";
import { CloseButton } from "../../../../../shared/components/closeButton";
import { ProfileBorderBox } from "../../../../../shared/components/profileComponents";

const QualificationEditView: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { loading, data, fetchData } = useQuery(`/qualification/${id}`, null);
  const { loading: apiLoading, modifyData } = useMutation();
  const [editStatus, setEditStatus] = useState(false);

  const qualificationData: Qualification = (data as any)?.data || {};

  //Edit permission checking
  const entityPermissions: any = (data as any)?.entityPermissions;
  const editPermission = entityPermissions
    ? entityPermissions?.includes("Qualification-Update")
    : true;
  //End of edit permission checking

  const initialValues = {
    name: qualificationData?.name ?? "",
    enabled: qualificationData?.enabled ? true : false,
    qualificationType: qualificationData?.qualificationType ?? null,
  };

  const qualificationForm = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validateOnBlur: true,

    validationSchema: customizeValidationSchema(),

    onSubmit: async (formData: any) => {
      let response;
      if (id) {
        response = await modifyData(
          `/qualification/${id}`,
          "put",
          {
            ...formData,
          },
          qualificationForm
        );
      }
      if (response) {
        closeEdit();
      }
    },
  });

  const closeEdit = () => {
    fetchData();
    setEditStatus(false);
  };

  const editTitlebar: TitleBarProps = {
    headerTitle: "Qualification -  Edit",
    title: "Edit Qualification",
    buttonTitle: "Save",
    onClick: () => qualificationForm.handleSubmit(),
    dirty: qualificationForm.dirty,
    buttonLoading: apiLoading,
    editCloseIcon: {
      visible: true,
      onClick: () => closeEdit(),
    },
  };

  const viewTitleBar: TitleBarProps = {
    title: "Qualification Details",
    headerTitle: "Qualification Details",
  };
  const qualificationEditFields = getTextFields(qualificationData);

  const closeDetailsView = () => {
    navigate("/master-datas/qualifications");
  };

  const qualificationEditTooltipNode = (
    <>
     {editPermission && <EditButton onClick={() => setEditStatus(true)} /> }
      <CloseButton onClick={() => closeDetailsView()} />
    </>
  );

  return (
    <>
      {editStatus && (
        <>
          <ProfileBorderBox>
            <FormContainer titleBar={editTitlebar}>
              <Box className="form-border-custom">
                <Grid
                  container
                  alignItems="stretch"
                  columnSpacing={4}
                  rowSpacing={2}
                >
                  <Grid item xs={12}>
                    <ToggleSwitch
                      status={qualificationForm?.values?.enabled}
                      handleChange={() =>
                        qualificationForm.setFieldValue(
                          "enabled",
                          !qualificationForm?.values?.enabled
                        )
                      }
                    />
                  </Grid>
                  <Grid item md={6} sm={6} xs={12} key="type">
                    <ZSSelectBox
                      fieldProps={{
                        id: "qualification-type",
                        name: "qualificationType",
                        placeholder: "Select qualification type",
                        label: "Qualification Type",
                        required: true,
                      }}
                      options={[
                        {
                          label: QualificationType.Graduation,
                          value: QualificationType.Graduation,
                        },
                        {
                          label: QualificationType.PostGraduation,
                          value: QualificationType.PostGraduation,
                        },
                        {
                          label: QualificationType.Certification,
                          value: QualificationType.Certification,
                        },
                        {
                          label: QualificationType.Diploma,
                          value: QualificationType.Diploma,
                        },
                        {
                          label: QualificationType.Other,
                          value: QualificationType.Other,
                        },
                      ]}
                      form={qualificationForm}
                      value={qualificationForm.values?.qualificationType}
                    />
                  </Grid>
                  {qualificationEditFields.map(
                    (field: any, index: number) =>
                      (field?.formField === undefined ||
                        field.formField === true) && (
                        <Grid item md={6} sm={6} xs={12} key={field.id}>
                          <InputTextfield
                            form={qualificationForm}
                            fieldProps={field}
                            key={index}
                          />
                        </Grid>
                      )
                  )}
                </Grid>
              </Box>
            </FormContainer>
          </ProfileBorderBox>
        </>
      )}

      {!editStatus && (
        <ProfileBorderBox>
          <ViewContainer loading={loading} titleBar={viewTitleBar}>
            <div className="entity-container">
              <SingleEntityContainer
                entityFields={qualificationEditFields}
                tooltips={qualificationEditTooltipNode}
              ></SingleEntityContainer>
            </div>
          </ViewContainer>
        </ProfileBorderBox>
      )}
    </>
  );
};

export default QualificationEditView;
